/**
 * stores/plan.ts
 *
 * Current plan (trial / free / pro), its limits, and the local daily sync quota.
 * The plan comes from the reactive `users/{uid}` document (see `paddleWebhook` for the Pro fields).
 */

import { doc, getFirestore } from 'firebase/firestore'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useDocument } from 'vuefire'
import { FREE_PLAN_LIMITS, TRIAL_DAYS } from '@/config/plan'
import { useRegistryStore } from '@/stores/registry'
import { useUserStore } from '@/stores/user'
import type { Plan, UserDoc } from '@/types/plan'
import { logger } from '@/utils/logger'

const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const DAILY_SYNCS_KEY = 'dailySyncs' // registry: value = syncs used, data = local day they were used on

// Local calendar day, e.g. '2026-10-01'
function dayKey (ms: number): string {
    const date = new Date(ms)
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

export const usePlanStore = defineStore('plan', () => {
    const userStore = useUserStore()
    const registryStore = useRegistryStore()

    // Coarse clock so time based getters (trial countdown, daily reset) refresh without page reloads
    const now = ref(Date.now())
    setInterval(() => {
        now.value = Date.now()
    }, 60_000)

    const userDocRef = computed(() => userStore.firebaseUid
        ? doc(getFirestore(), 'users', userStore.firebaseUid)
        : null)
    const { data: userDoc, pending: userDocPending } = useDocument<UserDoc>(userDocRef)
    watch(userDoc, data => {
        if (data === undefined) {
            return
        }

        logger.log('[Firestore] users document snapshot received')
        // logger.log('[Firestore] users document snapshot received', {
        //     uid: userStore.firebaseUid,
        //     exists: data !== null,
        //     plan: data?.plan ?? null,
        //     data: data ?? null,
        // })
    })

    const trialEndsAt = computed<number | null>(() => userDoc.value?.trialEndsAt?.toMillis() ?? null)

    // null until the user document has loaded; a trial past `trialEndsAt` counts as free even if
    // the backend hasn't flipped the stored plan yet (it does so on the next sign in)
    // undefined while the user document loads, null if it doesn't exist (treated as free)
    const plan = computed<Plan | null>(() => {
        const data = userDoc.value
        if (data === undefined) {
            return null
        }
        if (data === null) {
            return 'free'
        }
        if (data.plan === 'pro') {
            return 'pro'
        }
        if (data.plan === 'trial' && (trialEndsAt.value === null || trialEndsAt.value > now.value)) {
            return 'trial'
        }
        return 'free'
    })

    const isTrial = computed(() => plan.value === 'trial')
    const isFree = computed(() => plan.value === 'free')
    const isPro = computed(() => plan.value === 'pro')

    const subscription = computed(() => userDoc.value?.subscription ?? null)

    // Whether the user ever paid, to tell "trial ended" apart from "subscription ended"
    const hasHadSubscription = computed(() => !!subscription.value)

    const trialDaysLeft = computed<number | null>(() => {
        if (!isTrial.value || trialEndsAt.value === null) {
            return null
        }
        return Math.min(TRIAL_DAYS, Math.max(0, Math.ceil((trialEndsAt.value - now.value) / DAY_MS)))
    })

    // Trial expires in 24 hours or less
    const trialEndingSoon = computed(() => isTrial.value && trialEndsAt.value !== null && trialEndsAt.value - now.value <= DAY_MS)

    // null = unlimited
    const maxFolders = computed(() => isFree.value ? FREE_PLAN_LIMITS.maxFolders : null)
    const maxFiles = computed(() => isFree.value ? FREE_PLAN_LIMITS.maxFiles : null)

    // Daily sync quota (Free plan only), stored per user in the local `registry` table
    const syncsUsed = ref(0)
    const syncsDay = ref('')

    const syncsRemaining = computed<number | null>(() => {
        if (!isFree.value) {
            return null
        }
        const used = syncsDay.value === dayKey(now.value) ? syncsUsed.value : 0
        return Math.max(0, FREE_PLAN_LIMITS.dailySyncLimit - used)
    })

    const canSync = computed(() => syncsRemaining.value === null || syncsRemaining.value > 0)

    const hoursUntilSyncReset = computed(() => {
        const date = new Date(now.value)
        const nextMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime()
        return Math.max(1, Math.ceil((nextMidnight - now.value) / HOUR_MS))
    })

    /** Resolves once the plan is known, so limits aren't applied against a not yet loaded plan */
    function whenPlanLoaded (): Promise<Plan> {
        if (plan.value) {
            return Promise.resolve(plan.value)
        }

        return new Promise(resolve => {
            const stop = watch(plan, value => {
                if (value) {
                    stop()
                    resolve(value)
                }
            })
        })
    }

    async function loadLocalState () {
        const entry = await registryStore.getEntry(DAILY_SYNCS_KEY)
        syncsUsed.value = typeof entry?.value === 'number' ? entry.value : 0
        syncsDay.value = entry?.data ?? ''
    }

    function clearLocalState () {
        syncsUsed.value = 0
        syncsDay.value = ''
    }

    /** Counts one folder sync against today's quota */
    async function consumeSync () {
        const today = dayKey(Date.now())
        const used = syncsDay.value === today ? syncsUsed.value : 0

        syncsUsed.value = used + 1
        syncsDay.value = today
        await registryStore.setEntry(DAILY_SYNCS_KEY, syncsUsed.value, today)
    }

    return {
        userDoc,
        userDocPending,
        plan,
        isTrial,
        isFree,
        isPro,
        subscription,
        hasHadSubscription,
        trialEndsAt,
        trialDaysLeft,
        trialEndingSoon,
        maxFolders,
        maxFiles,
        syncsRemaining,
        canSync,
        hoursUntilSyncReset,
        whenPlanLoaded,
        loadLocalState,
        clearLocalState,
        consumeSync,
    }
})
